// Matches network-level noise (no HTTP response) that must not raise the crash report dialog.
const NETWORK_ERROR_PATTERN = /network error|err_network|networkerror|failed to fetch|load failed/i;

/**
 * Decide whether the crash report dialog should be suppressed for a captured event.
 * Network-level noise (transient connection failures, cancelled requests, the console-captured
 * Pusher auth failure) is still sent to Sentry, but must not raise a blocking dialog. Genuine
 * 4xx/5xx server and auth errors still surface the dialog, unless the request explicitly
 * flagged that particular status as an expected, already-handled outcome (see
 * `expectedErrorStatuses` in helpers/network.js) — those are tagged `suppress_report_dialog`
 * by the axios interceptor and are caught by the tag check below.
 */
export function shouldSuppressReportDialog(event, hint) {
    // Deterministic axios path: tag values are serialised to the string 'true', so check truthy.
    if (event.tags && event.tags.suppress_report_dialog) {
        return true;
    }

    const original = hint?.originalException;
    if (original) {
        if (original.code === 'ERR_NETWORK' || original.code === 'ERR_CANCELED') {
            return true;
        }
        if (original.request && !original.response) {
            return true;
        }
    }

    const values = event.exception?.values;
    if (Array.isArray(values)) {
        return values.some((value) => {
            const text = `${value.type || ''} ${value.value || ''}`;

            if (NETWORK_ERROR_PATTERN.test(text)) {
                return true;
            }

            // Console-captured Pusher auth failure (bypasses axios).
            return /pusher/i.test(text) && /auth/i.test(text);
        });
    }

    return false;
}
